export interface SiteLineSession {
  uid: string; principalUid: string; tenantId: string; branchId: string;
  organizationName?: string;
  product: 'siteline'; role: string; orgRole: string; tier: string; csrf: string;
  assurance: 'primary' | 'totp' | 'passkey'; expiresAt: number;
  profile?: { email: string | null; emailVerified: boolean; displayName: string | null };
}
export class SiteLineIdentityError extends Error {
  constructor(readonly code: string, readonly status = 503, readonly retryAfterMs = 0) { super(code); }
}
export type IdentityLanding = { kind: 'callback'; code: string; requestId: string }
  | { kind: 'start'; tenantId: string; branchId: string | null } | { kind: 'invalid' } | null;
/** Must run before importing the legacy application or telemetry. */
export function consumeIdentityLanding(location: Pick<Location, 'pathname' | 'hash' | 'search'>, history: Pick<History, 'replaceState'>): IdentityLanding {
  if (location.pathname === '/identity/callback') {
    const match = /^#code=([A-Za-z0-9_-]{43})&request=([A-Za-z0-9_-]{43})$/.exec(location.hash);
    const query = location.search;
    history.replaceState(null, '', '/identity/callback');
    return match && !query ? { kind: 'callback', code: match[1], requestId: match[2] } : { kind: 'invalid' };
  }
  if (location.pathname !== '/identity/start') return null;
  const params = new URLSearchParams(location.search);
  const tenantId = params.get('tenant'); const branchId = params.get('branch');
  const valid = (value: string | null) => !!value && value.length <= 128 && !value.includes('/') && !['.', '..'].includes(value);
  const invalid = !!location.hash || !valid(tenantId) || !valid(branchId)
    || [...params.keys()].some(key => !['tenant', 'branch'].includes(key)) || params.getAll('tenant').length !== 1 || params.getAll('branch').length > 1;
  history.replaceState(null, '', '/identity/start');
  return invalid ? { kind: 'invalid' } : { kind: 'start', tenantId: tenantId!, branchId };
}

export function createSiteLineIdentityClient(fetcher: typeof fetch = fetch) {
  let sessionRetryAt = 0;
  async function request(action: string, body: object = {}, csrf?: string, signal?: AbortSignal) {
    const controller = new AbortController(); const timeout = setTimeout(() => controller.abort(), 20000);
    const abort = () => controller.abort();
    signal?.addEventListener('abort', abort, { once: true });
    if (signal?.aborted) controller.abort();
    try {
      const response = await fetcher(`/api/identity/${action}`, { method: 'POST', credentials: 'same-origin', cache: 'no-store',
        redirect: 'error', signal: controller.signal, headers: { 'Content-Type': 'application/json', ...(csrf ? { 'X-TriAxis-CSRF': csrf } : {}) }, body: JSON.stringify(body) });
      if (action === 'logout' && response.status === 204) return {};
      if (action === 'logout' && response.ok) throw new SiteLineIdentityError('logout_not_confirmed');
      const value = await response.json().catch(() => null);
      if (!response.ok) {
        const retry = response.headers.get('Retry-After');
        const retryAfterMs = retry ? (/^\d+$/.test(retry) ? Number(retry) * 1000 : Date.parse(retry) - Date.now()) : 0;
        throw new SiteLineIdentityError(typeof value?.error === 'string' ? value.error : 'identity_service_unavailable', response.status,
          Number.isFinite(retryAfterMs) ? Math.max(0, retryAfterMs) : 0);
      }
      if (!value || typeof value !== 'object' || Array.isArray(value)) throw new SiteLineIdentityError('invalid_identity_response');
      return value as Record<string, unknown>;
    } catch (error) { if (error instanceof SiteLineIdentityError) throw error; throw new SiteLineIdentityError('identity_service_unavailable'); }
    finally { clearTimeout(timeout); signal?.removeEventListener('abort', abort); }
  }
  async function session(signal?: AbortSignal): Promise<SiteLineSession | null> {
    if (Date.now() < sessionRetryAt) throw new SiteLineIdentityError('rate_limited', 429, sessionRetryAt - Date.now());
    let row;
    // Retry this read only. Never replay a handoff, logout or product mutation.
    // The lifecycle keeps its existing descriptor only until its original expiry.
    const retryUntil = Date.now() + 12000;
    for (let attempt = 0; ; attempt++) {
      if (signal?.aborted) throw new SiteLineIdentityError('session_check_cancelled');
      try { row = await request('session', {}, undefined, signal); sessionRetryAt = 0; break; } catch (error) {
        if (error instanceof SiteLineIdentityError && error.status === 401 && ['session_required', 'session_expired'].includes(error.code)) return null;
        const transient = error instanceof SiteLineIdentityError && [408, 429, 500, 502, 503, 504].includes(error.status)
          && error.code !== 'invalid_identity_response';
        if (error instanceof SiteLineIdentityError && error.status === 429) sessionRetryAt = Date.now() + Math.max(error.retryAfterMs, 60000);
        const delay = Math.max(400 * 3 ** attempt + Math.floor(Math.random() * 200), sessionRetryAt - Date.now(), error instanceof SiteLineIdentityError ? error.retryAfterMs : 0);
        if (!transient || attempt >= 2 || signal?.aborted || Date.now() + delay >= retryUntil) throw error;
        await new Promise<void>(resolve => {
          const done = () => { clearTimeout(timer); signal?.removeEventListener('abort', done); resolve(); };
          const timer = setTimeout(done, delay); signal?.addEventListener('abort', done, { once: true });
          if (signal?.aborted) done();
        });
      }
    }
    const validId = (value: unknown) => typeof value === 'string' && value.length > 0 && value.length <= 128 && !value.includes('/') && !['.', '..'].includes(value);
    const fields = ['uid', 'principalUid', 'tenantId', 'orgRole'];
    if (fields.some(key => !validId(row[key])) || row.product !== 'siteline'
      || !validId(row.branchId) || !['primary', 'totp', 'passkey'].includes(String(row.assurance))
      || !['admin', 'manager', 'project_manager', 'supervisor', 'technician', 'accounting', 'compliance_officer', 'viewer'].includes(String(row.role)) || !['starter', 'core', 'growth', 'enterprise', 'sovereign'].includes(String(row.tier))
      || typeof row.csrf !== 'string' || !/^[A-Za-z0-9_-]{43}$/.test(row.csrf)
      || typeof row.expiresAt !== 'number' || !Number.isFinite(row.expiresAt) || row.expiresAt <= Date.now()) throw new SiteLineIdentityError('invalid_identity_response');
    if (row.organizationName !== undefined && (typeof row.organizationName !== 'string' || !row.organizationName.trim()
      || row.organizationName.length > 160 || Array.from(row.organizationName).some(char => char.charCodeAt(0) < 32 || char.charCodeAt(0) === 127))) delete row.organizationName;
    if (row.profile !== undefined) {
      const profile = row.profile as SiteLineSession['profile'];
      const hasControls = (value: string) => Array.from(value).some(char => char.charCodeAt(0) < 32 || char.charCodeAt(0) === 127);
      if (!profile || typeof profile !== 'object' || Array.isArray(profile) || typeof profile.emailVerified !== 'boolean'
        || (profile.email !== null && (typeof profile.email !== 'string' || profile.email.length > 320 || hasControls(profile.email) || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(profile.email)))
        || profile.emailVerified !== (profile.email !== null)
        || (profile.displayName !== null && (typeof profile.displayName !== 'string' || !profile.displayName.trim() || profile.displayName.length > 160 || hasControls(profile.displayName)))
        || Object.keys(profile).some(key => !['email', 'emailVerified', 'displayName'].includes(key))) throw new SiteLineIdentityError('invalid_identity_response');
    }
    return row as unknown as SiteLineSession;
  }
  return {
    session,
    async begin(tenantId: string, branchId: string | null): Promise<string> {
      const row = await request('begin', { tenantId, branchId });
      if (typeof row.redirect !== 'string' || !/^https:\/\/triaxis-labs\.com\/identity\/launch#request=[A-Za-z0-9_-]{43}$/.test(row.redirect)) throw new SiteLineIdentityError('invalid_identity_response');
      return row.redirect;
    },
    async complete(code: string, requestId: string): Promise<SiteLineSession> {
      if (![code, requestId].every(value => /^[A-Za-z0-9_-]{43}$/.test(value))) throw new SiteLineIdentityError('handoff_rejected', 400);
      const transaction = await request('transaction');
      if (transaction.requestId !== requestId || typeof transaction.csrf !== 'string' || !/^[A-Za-z0-9_-]{43}$/.test(transaction.csrf)) throw new SiteLineIdentityError('handoff_rejected', 401);
      await request('exchange', { code, requestId }, transaction.csrf);
      const result = await session(); if (!result) throw new SiteLineIdentityError('session_not_confirmed');
      return result;
    },
    async logout(csrf: string) {
      if (!csrf) {
        try {
          const row = await request('transaction');
          if (typeof row.csrf !== 'string' || !/^[A-Za-z0-9_-]{43}$/.test(row.csrf)) throw new SiteLineIdentityError('invalid_identity_response');
          csrf = row.csrf;
        } catch (error) {
          // No cookie: the logout endpoint can confirm the empty session.
          if (!(error instanceof SiteLineIdentityError) || error.code !== 'handoff_expired') throw error;
        }
      }
      await request('logout', {}, csrf);
    },
  };
}
